import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth, ROLES } from '../../hooks/useAuth';
import { ROUTES, APP_NAME, APP_VERSION, FIREBASE_CONFIG, ORDER_STATUS } from '../../constants';
import { formatPrice, getOrderQuantity } from '../../utils';
import { exportData, importData, validateBackupFile } from '../../utils/backupUtils';
import CustomerList from '../customers/CustomerList';
import ProductList from '../productList/ProductList';
// import AccountingReport from '../accounting/AccountingReport'; // GEÇİCİ: Hesaplamalar sekmesi devre dışı

import './Dashboard.css';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userRole, permissions, isAdmin, isMontajci, isYazilimci, isSatisDanismani, loading } = useAuth();

  // LocalStorage'dan son sekmeyi oku
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab || 'dashboard';
  });

  // Rol değiştiğinde veya yüklendiğinde erişim kontrolü
  useEffect(() => {
    if (permissions) {
      // Satışcı dashboard'a erişemez, customers'a yönlendir
      if (!permissions.canAccessDashboard && activeTab === 'dashboard') {
        setActiveTab('customers');
      }
      // Montajcı sadece customers'a erişebilir
      if (isMontajci && activeTab !== 'customers') {
        setActiveTab('customers');
      }
      // Kullanıcı yönetimine erişim yoksa ve users sekmesindeyse
      if (!isAdmin && activeTab === 'users') {
        setActiveTab('customers');
      }
    }
  }, [permissions, activeTab, isMontajci, isAdmin]);
  const [customerStats, setCustomerStats] = useState({
    total: 0,
    active: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0
  });
  const [revenueStats, setRevenueStats] = useState({
    expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
    total: 0,
    remainingTotal: 0, // Kalan ödemeler toplamı
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0,
    todayCost: 0,
    thisWeekCost: 0,
    thisMonthCost: 0,
    thisYearCost: 0,
    todayNet: 0,
    thisWeekNet: 0,
    thisMonthNet: 0,
    thisYearNet: 0,
    // Ödeme tipine göre günlük gelirler
    todayCash: 0, // Bugün Elden Nakit
    todayIban: 0, // Bugün IBAN
    yesterdayCreditCard: 0 // Dün Kredi Kartı (bir gün gecikme)
  });
  const [lastYearStats, setLastYearStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  const [monthlyOrderTrend, setMonthlyOrderTrend] = useState([]);
  const [monthlyCustomerTrend, setMonthlyCustomerTrend] = useState([]);
  const [monthlyEarningsTrend, setMonthlyEarningsTrend] = useState([]);
  const [monthlyNetEarningsTrend, setMonthlyNetEarningsTrend] = useState([]);
  const [deliveryReminders, setDeliveryReminders] = useState({
    overdue: [], // Geciken teslimatlar
    today: [], // Bugün teslim edilecek
    tomorrow: [], // Yarın teslim edilecek
    thisWeek: [] // Bu hafta teslim edilecek (2-7 gün)
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsLastUpdated, setStatsLastUpdated] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);

  //Firma bilgileri state'leri
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    phone: ''
  });
  const [companyError, setCompanyError] = useState(null);
  const [companySuccess, setCompanySuccess] = useState(false);
  const [deleteCompanyModal, setDeleteCompanyModal] = useState(null); // Silinecek firma bilgisi

  //Şifre değiştirme state'leri
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showPasswordConfirmModal, setShowPasswordConfirmModal] = useState(false);

  // Kullanıcı Yönetimi State'leri
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const YAZILIMCI_EMAIL = 'kahramantechinfo@gmail.com';

  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    displayName: '',
    role: ROLES.MONTAJCI
  });
  const [userFormError, setUserFormError] = useState(null);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [deleteUserModal, setDeleteUserModal] = useState(null);

  // Notification Modal State
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    type: 'success', // 'success', 'error', 'warning', 'info'
    title: '',
    message: '',
    details: null
  });

  // Notification Modal göster
  const showNotificationModal = (type, title, message, details = null) => {
    setNotificationModal({
      show: true,
      type,
      title,
      message,
      details
    });
  };

  // Notification Modal kapat
  const closeNotificationModal = () => {
    setNotificationModal({
      show: false,
      type: 'success',
      title: '',
      message: '',
      details: null
    });
  };

  // Tüm istatistikleri çek
  const fetchAllStats = useCallback(async (forceRefresh = false) => {
    try {
      setIsLoadingStats(true);

      // 30 dakikalık cache kontrolü (Firebase Read kotasını korumak için)
      if (!forceRefresh) {
        const cachedData = localStorage.getItem('dashboard_stats_cache');
        const cachedTime = localStorage.getItem('dashboard_stats_time');
        
        if (cachedData && cachedTime) {
          const ageInMs = Date.now() - parseInt(cachedTime, 10);
          const cacheLimit = 30 * 60 * 1000; // 30 Dakika
          if (ageInMs < cacheLimit) {
            const parsed = JSON.parse(cachedData);
            setCustomerStats(parsed.customerStats);
            setRevenueStats(parsed.revenueStats);
            setLastYearStats(parsed.lastYearStatsTemp);
            setMonthlyOrderTrend(parsed.monthlyTrendData);
            setMonthlyCustomerTrend(parsed.monthlyCustomerTrendData);
            setMonthlyEarningsTrend(parsed.monthlyEarningsTrendData);
            setMonthlyNetEarningsTrend(parsed.monthlyNetEarningsTrendData);
            setDeliveryReminders(parsed.deliveryData);
            setStatsLastUpdated(new Date(parseInt(cachedTime, 10)));
            setIsLoadingStats(false);
            return;
          }
        }
      }

      // Yazılımcı/Rol kısıtlaması yoksa stats/dashboard birleştirilmiş belgesini (Materialized View) kontrol et
      const isFilteredView = isYazilimci && user?.uid;
      if (!forceRefresh && !isFilteredView) {
        try {
          const statsDocRef = doc(db, 'stats', 'dashboard');
          const statsDocSnap = await getDoc(statsDocRef);
          if (statsDocSnap.exists()) {
            const data = statsDocSnap.data();
            const updatedAt = data.updatedAt || Date.now();
            const ageInMs = Date.now() - updatedAt;
            const docLimit = 4 * 60 * 60 * 1000; // 4 Saat geçerlilik süresi
            
            if (ageInMs < docLimit) {
              setCustomerStats(data.customerStats);
              setRevenueStats(data.revenueStats);
              setLastYearStats(data.lastYearStatsTemp);
              setMonthlyOrderTrend(data.monthlyTrendData);
              setMonthlyCustomerTrend(data.monthlyCustomerTrendData);
              setMonthlyEarningsTrend(data.monthlyEarningsTrendData);
              setMonthlyNetEarningsTrend(data.monthlyNetEarningsTrendData);
              setDeliveryReminders(data.deliveryData);
              setStatsLastUpdated(new Date(updatedAt));

              // Local cache'i de bu veriyle doldur ki sekme geçişlerinde sıfır veritabanı okuması yapılsın
              const dataToCache = {
                customerStats: data.customerStats,
                revenueStats: data.revenueStats,
                lastYearStatsTemp: data.lastYearStatsTemp,
                monthlyTrendData: data.monthlyTrendData,
                monthlyCustomerTrendData: data.monthlyCustomerTrendData,
                monthlyEarningsTrendData: data.monthlyEarningsTrendData,
                monthlyNetEarningsTrendData: data.monthlyNetEarningsTrendData,
                deliveryData: data.deliveryData
              };
              localStorage.setItem('dashboard_stats_cache', JSON.stringify(dataToCache));
              localStorage.setItem('dashboard_stats_time', updatedAt.toString());

              setIsLoadingStats(false);
              return;
            }
          }
        } catch (docErr) {
          console.warn('Birleştirilmiş istatistik belgesi okunamadı, canlı hesaplamaya geçiliyor:', docErr);
        }
      }

      // Müşterileri çek
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      let customers = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Yazılımcı rolü için sadece kendi oluşturduğu müşterileri filtrele
      if (isYazilimci && user?.uid) {
        customers = customers.filter(c => c.createdBy === user.uid);
      }

      // Siparişleri çek
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      let orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Yazılımcı rolü için sadece kendi müşterilerinin siparişlerini filtrele
      if (isYazilimci && user?.uid) {
        const customerIds = customers.map(c => c.id);
        orders = orders.filter(o => customerIds.includes(o.customerId));
      }

      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastYear = now.getFullYear() - 1;
      const lastYearYearStart = new Date(lastYear, 0, 1);
      const lastYearYearEnd = new Date(lastYear, 11, 31, 23, 59, 59, 999);
      const lastYearTodayStart = new Date(lastYear, now.getMonth(), now.getDate());
      const lastYearTodayEnd = new Date(lastYear, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearWeekStart = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate() - 6);
      const lastYearWeekEnd = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearMonthStart = new Date(lastYear, now.getMonth(), 1);
      const lastYearMonthEnd = new Date(lastYear, now.getMonth() + 1, 0, 23, 59, 59, 999);

      // Müşteri istatistikleri
      const customerStats = {
        total: customers.length,
        active: customers.filter(customer => {
          // Sipariş durumu "Yeni Sipariş" olan müşteriler
          return customer.orderStatus === 'new_order';
        }).length,
        today: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bugün (aynı gün kontrolü)
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const customerDate = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
          return customerDate.getTime() === today.getTime();
        }).length,
        thisWeek: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Son 7 gün (bugün dahil)
          const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
          sevenDaysAgo.setHours(0, 0, 0, 0);
          return orderDate >= sevenDaysAgo;
        }).length,
        thisMonth: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu ay sipariş veren müşteriler
          return orderDate >= thisMonth;
        }).length,
        thisYear: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu yıl sipariş veren müşteriler (yılın ilk günü)
          const thisYearStart = new Date(now.getFullYear(), 0, 1);
          return orderDate >= thisYearStart;
        }).length
      };

      // Gelir istatistikleri
      const revenueStats = {
        expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
        total: 0,
        totalCost: 0, // Tüm siparişlerin maliyet toplamı
        netTotal: 0, // Toplam Gelir (Tahsilat) - Toplam Maliyet
        expectedNet: 0, // Toplam Sipariş Tutarı - Toplam Maliyet
        remainingTotal: 0, // Kalan ödemeler toplamı (Atölye + Firma + Montaj + Teslim Edildi)
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0,
        todayCost: 0,
        thisWeekCost: 0,
        thisMonthCost: 0,
        thisYearCost: 0,
        todayExpected: 0,
        thisWeekExpected: 0,
        thisMonthExpected: 0,
        thisYearExpected: 0,
        todayNet: 0,
        thisWeekNet: 0,
        thisMonthNet: 0,
        thisYearNet: 0,
        // Ödeme tipine göre günlük gelirler
        todayCash: 0,
        todayIban: 0,
        yesterdayCreditCard: 0
      };

      const lastYearStatsTemp = {
        total: 0,
        today: 0,
        thisWeek: 0,
        thisMonth: 0
      };

      // Her müşteri için ödenen tutarları kontrol et (gerçek kazanç)
      customers.forEach(customer => {
        const isNewOrder = customer.orderStatus === ORDER_STATUS.NEW_ORDER;

        // Müşterinin ödenen tutarını al (gerçek kazanç)
        const paidAmount = parseFloat(customer.paidAmount || 0);
        let customerTotalCost = 0;
        let customerTotalRevenue = 0;

        // Toplam Gelir Beklentisi - Tüm siparişler için beklenen gelir
        // Müşterinin siparişlerini bul
        const customerOrders = orders.filter(order => order.customerId === customer.id);

        if (customerOrders.length > 0) {
          // Toplam tutar
          const totalAmount = customerOrders.reduce((sum, order) => {
            return sum + parseFloat(order.totalPrice || 0);
          }, 0);

          // İndirimli tutar
          const discountedTotalAmount = customerOrders.reduce((sum, order) => {
            if (order.discountedPrice && parseFloat(order.discount) > 0) {
              return sum + parseFloat(order.discountedPrice || 0);
            } else {
              return sum + parseFloat(order.totalPrice || 0);
            }
          }, 0);

          // Ahşap Jaluzi toplamı
          const ahsapJaluziTotal = customerOrders.reduce((sum, order) => {
            if (order.productType === 'ahsapJaluzi') {
              return sum + parseFloat(order.totalPrice || 0);
            }
            return sum;
          }, 0);

          // Beklenen gelir hesaplama (Hiyerarşi: Manuel > Vade > Genel İskonto > Tekil İskonto > Ara Toplam)
          const vadeliYuzde = parseFloat(customer.vadeliYuzde) || 0;
          customerTotalRevenue = 0;
          
          if (customer.manualGrandTotal !== undefined && customer.manualGrandTotal !== null && customer.manualGrandTotal !== '') {
            // 1. Manuel Genel Toplam
            customerTotalRevenue = parseFloat(customer.manualGrandTotal);
          } else if (vadeliYuzde > 0) {
            // 2. Vadeli Hesaplama
            const vadeliTutar = totalAmount - ahsapJaluziTotal;
            const nakitOdeme = ahsapJaluziTotal;
            customerTotalRevenue = (vadeliTutar + (vadeliTutar * vadeliYuzde / 100)) + nakitOdeme;
          } else if (customer.genelIskontoYuzde && parseFloat(customer.genelIskontoYuzde) > 0) {
            // 3. Genel İskonto
            const gIskonto = parseFloat(customer.genelIskontoYuzde);
            customerTotalRevenue = totalAmount - (totalAmount * gIskonto / 100);
          } else if (discountedTotalAmount !== totalAmount && discountedTotalAmount > 0) {
            // 4. İndirimli tutar varsa
            customerTotalRevenue = discountedTotalAmount;
          } else {
            // 5. Sadece toplam tutar
            customerTotalRevenue = totalAmount;
          }

          revenueStats.expectedTotal += customerTotalRevenue;
          
          // Tüm siparişlerin maliyetini hesapla (Potansiyel Net Kazanç için)
          customerTotalCost = customerOrders.reduce((sum, order) => {
            return sum + parseFloat(order.totalCost || 0);
          }, 0);

          // Sadece onaylı siparişlerin maliyetini toplam maliyete ekle (Net Gelir için)
          if (!isNewOrder) {
            revenueStats.totalCost += customerTotalCost;
          }

          // Beklenen Net Kazanç (Satış - Maliyet) - Yeni siparişler dahil, maliyetli hesaplanır
          revenueStats.expectedNet += (customerTotalRevenue - customerTotalCost);
        }

        // Toplam Gelir - Ödenen tutarlar (gerçek kazanç) - Yeni siparişler dahil edilmez
        if (!isNewOrder) {
          revenueStats.total += paidAmount;
        }

        // Kalan Ödemeler - Sadece Atölye + Firma + Montaj + Teslim Edildi durumundaki müşterilerin kalan ödemeleri
        if (['atolye', 'firma', 'montaj', 'delivered'].includes(customer.orderStatus)) {
          const remainingAmount = parseFloat(customer.remainingAmount || 0);
          revenueStats.remainingTotal += remainingAmount;
        }

        // Ödeme geçmişinden ödeme tipine göre günlük gelirleri hesapla (sipariş tarihinden bağımsız)
        const paymentHistory = customer.paymentHistory || [];
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

        paymentHistory.forEach(payment => {
          // Firestore Timestamp veya ISO string olabilir
          let paymentDate;
          if (payment.date?.toDate) {
            paymentDate = payment.date.toDate();
          } else if (payment.date?.seconds) {
            paymentDate = new Date(payment.date.seconds * 1000);
          } else {
            paymentDate = new Date(payment.date);
          }
          const paymentDateOnly = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), paymentDate.getDate());

          // Bugün Elden Nakit
          if (payment.type === 'cash' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayCash += payment.amount;
          }

          // Bugün IBAN
          if (payment.type === 'iban' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayIban += payment.amount;
          }

          // Dün Kredi Kartı (kredi kartı ödemeleri bir gün gecikmeyle yansır)
          if (payment.type === 'credit_card' && paymentDateOnly.getTime() === yesterday.getTime()) {
            revenueStats.yesterdayCreditCard += payment.amount;
          }
        });

        // Sipariş tarihi yoksa zaman bazlı istatistiklere dahil etme
        if (!customer.orderDate) return;

        // Müşterinin sipariş tarihini al
        let customerDate;
        if (customer.orderDate?.toDate) {
          customerDate = customer.orderDate.toDate();
        } else {
          customerDate = new Date(customer.orderDate);
        }

        // Bugün - Bugün sipariş veren müşterilerin ödenen tutarları
        const customerDateOnly = new Date(customerDate.getFullYear(), customerDate.getMonth(), customerDate.getDate());
        if (customerDateOnly.getTime() === today.getTime()) {
          if (!isNewOrder) {
            revenueStats.today += paidAmount;
            revenueStats.todayCost += customerTotalCost;
          }
          revenueStats.todayExpected += customerTotalRevenue;
        }

        // Bu hafta - Son 7 gün içinde sipariş veren müşterilerin ödenen tutarları (bugün dahil)
        const sevenDaysAgoRevenue = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        sevenDaysAgoRevenue.setHours(0, 0, 0, 0);
        if (customerDate >= sevenDaysAgoRevenue) {
          if (!isNewOrder) {
            revenueStats.thisWeek += paidAmount;
            revenueStats.thisWeekCost += customerTotalCost;
          }
          revenueStats.thisWeekExpected += customerTotalRevenue;
        }

        // Bu ay - Bu ay sipariş veren müşterilerin ödenen tutarları
        if (customerDate >= thisMonth) {
          if (!isNewOrder) {
            revenueStats.thisMonth += paidAmount;
            revenueStats.thisMonthCost += customerTotalCost;
          }
          revenueStats.thisMonthExpected += customerTotalRevenue;
        }

        // Bu yıl - Bu yıl sipariş veren müşterilerin ödenen tutarları
        const thisYearStartForNet = new Date(now.getFullYear(), 0, 1);
        if (customerDate >= thisYearStartForNet) {
          if (!isNewOrder) {
            revenueStats.thisYear += paidAmount;
            revenueStats.thisYearCost += customerTotalCost;
          }
          revenueStats.thisYearExpected += customerTotalRevenue;
        }

        // Geçen yıl karşılaştırmaları
        if (customerDate >= lastYearYearStart && customerDate <= lastYearYearEnd) {
          lastYearStatsTemp.total += 1;
        }

        if (customerDate >= lastYearTodayStart && customerDate <= lastYearTodayEnd) {
          lastYearStatsTemp.today += 1;
        }

        if (customerDate >= lastYearWeekStart && customerDate <= lastYearWeekEnd) {
          lastYearStatsTemp.thisWeek += 1;
        }

        if (customerDate >= lastYearMonthStart && customerDate <= lastYearMonthEnd) {
          lastYearStatsTemp.thisMonth += 1;
        }
      });

      // Net Kazanç hesaplama (Ödenen - Maliyet)
      revenueStats.netTotal = revenueStats.total - revenueStats.totalCost;
      revenueStats.todayNet = revenueStats.today - revenueStats.todayCost;
      revenueStats.thisWeekNet = revenueStats.thisWeek - revenueStats.thisWeekCost;
      revenueStats.thisMonthNet = revenueStats.thisMonth - revenueStats.thisMonthCost;
      revenueStats.thisYearNet = revenueStats.thisYear - revenueStats.thisYearCost;

      // Aylık sipariş trendi (mevcut yıl)
      const monthLabels = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      const currentYear = now.getFullYear();
      const monthlyTrendCounts = Array(12).fill(0);
      const monthlyCustomerCounts = Array(12).fill(0);

      // Sipariş tarihlerini müşteriden okur (orders içinde orderDate yok)
      const customerOrderDateMap = new Map();
      const customerStatusMap = new Map();
      customers.forEach(customer => {
        const dateToUse = customer.orderDate;
        let parsed = null;
        if (dateToUse?.toDate) {
          parsed = dateToUse.toDate();
        } else if (dateToUse) {
          parsed = new Date(dateToUse);
        }
        if (parsed && !isNaN(parsed.getTime())) {
          customerOrderDateMap.set(customer.id, parsed);
        }
        customerStatusMap.set(customer.id, customer.orderStatus);
      });

      orders.forEach(order => {
        // Sipariş Tarihi müşteri kaydından alınır; yoksa ekleme
        const orderDate = customerOrderDateMap.get(order.customerId);
        if (!orderDate) return;
        if (orderDate.getFullYear() === currentYear) {
          monthlyTrendCounts[orderDate.getMonth()] += getOrderQuantity(order);
        }
      });

      customers.forEach(customer => {
        // Müşteri trendi de sipariş tarihine göre; tarih yoksa ekleme
        const dateToUse = customer.orderDate;
        let customerDate = null;

        if (dateToUse?.toDate) {
          customerDate = dateToUse.toDate();
        } else if (dateToUse) {
          customerDate = new Date(dateToUse);
        }

        if (!customerDate || isNaN(customerDate.getTime())) return;

        if (customerDate.getFullYear() === currentYear) {
          monthlyCustomerCounts[customerDate.getMonth()] += 1;
        }
      });

      const monthlyTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyTrendCounts[index]
      }));

      const monthlyCustomerTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyCustomerCounts[index]
      }));

      // Aylık Kazanç Trendi Hesaplama
      const monthlyEarningsCounts = Array(12).fill(0);
      const monthlyNetEarningsCounts = Array(12).fill(0);

      customers.forEach(customer => {
        // Müşterinin sipariş tarihini al
        const dateToUse = customer.orderDate;
        let customerDate = null;

        if (dateToUse?.toDate) {
          customerDate = dateToUse.toDate();
        } else if (dateToUse) {
          customerDate = new Date(dateToUse);
        }

        if (!customerDate || isNaN(customerDate.getTime())) return;
        if (customerDate.getFullYear() !== currentYear) return;

        // Müşterinin ödenen tutarını al (gerçek kazanç)
        const paidAmount = parseFloat(customer.paidAmount || 0);

        // Aylık kazanca ekle (ödenen tutar)
        monthlyEarningsCounts[customerDate.getMonth()] += paidAmount;

        const customerOrders = orders.filter(order => order.customerId === customer.id);
        const customerTotalCost = customerOrders.reduce((sum, order) => {
          return sum + parseFloat(order.totalCost || 0);
        }, 0);

        // Bu müşteri için beklenen toplam tutar üzerinden mi yoksa alınan ödeme üzerinden mi net gidilecek?
        // Kullanıcı "toplam tutardan maliyeti çıkarıp net kazanç bilgisini verecek" demiş. 
        // Bu nedenle o ayki "Alınan Ödeme - Toplam Maliyet" net kazançtır.
        monthlyNetEarningsCounts[customerDate.getMonth()] += (paidAmount - customerTotalCost);
      });

      const monthlyEarningsTrendData = monthLabels.map((label, index) => ({
        month: label,
        earnings: monthlyEarningsCounts[index]
      }));

      const monthlyNetEarningsTrendData = monthLabels.map((label, index) => ({
        month: label,
        netEarnings: monthlyNetEarningsCounts[index]
      }));

      // Teslimat Hatırlatıcı Hesaplaması
      const todayDelivery = new Date();
      todayDelivery.setHours(0, 0, 0, 0);

      const tomorrow = new Date(todayDelivery);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const weekEnd = new Date(todayDelivery);
      weekEnd.setDate(weekEnd.getDate() + 7);

      const deliveryData = {
        overdue: [],
        today: [],
        tomorrow: [],
        thisWeek: []
      };

      // Teslim edilmemiş müşterileri filtrele (delivered hariç)
      const pendingCustomers = customers.filter(c =>
        c.orderStatus && c.orderStatus !== 'delivered' && c.deliveryDate
      );

      pendingCustomers.forEach(customer => {
        let deliveryDate;
        if (customer.deliveryDate?.toDate) {
          deliveryDate = customer.deliveryDate.toDate();
        } else {
          deliveryDate = new Date(customer.deliveryDate);
        }
        deliveryDate.setHours(0, 0, 0, 0);

        const customerInfo = {
          id: customer.id,
          name: customer.name,
          phone: customer.phone1,
          address: customer.address,
          deliveryDate: customer.deliveryDate,
          orderStatus: customer.orderStatus,
          daysLeft: Math.ceil((deliveryDate - todayDelivery) / (1000 * 60 * 60 * 24))
        };

        if (deliveryDate < todayDelivery) {
          // Gecikmiş
          customerInfo.daysOverdue = Math.abs(customerInfo.daysLeft);
          deliveryData.overdue.push(customerInfo);
        } else if (deliveryDate.getTime() === todayDelivery.getTime()) {
          // Bugün
          deliveryData.today.push(customerInfo);
        } else if (deliveryDate.getTime() === tomorrow.getTime()) {
          // Yarın
          deliveryData.tomorrow.push(customerInfo);
        } else if (deliveryDate < weekEnd) {
          // Bu hafta (2-7 gün)
          deliveryData.thisWeek.push(customerInfo);
        }
      });

      // Gecikmişleri en eski tarihten yeniye sırala
      deliveryData.overdue.sort((a, b) => b.daysOverdue - a.daysOverdue);

      // Bu haftakileri en yakından en uzaka (kalan gün sayısına göre küçükten büyüğe) sırala
      deliveryData.thisWeek.sort((a, b) => a.daysLeft - b.daysLeft);

      setCustomerStats(customerStats);
      setRevenueStats(revenueStats);
      setLastYearStats(lastYearStatsTemp);
      setMonthlyOrderTrend(monthlyTrendData);
      setMonthlyCustomerTrend(monthlyCustomerTrendData);
      setMonthlyEarningsTrend(monthlyEarningsTrendData);
      setMonthlyNetEarningsTrend(monthlyNetEarningsTrendData);
      setDeliveryReminders(deliveryData);

      // Cache'e kaydet (Firebase Read kotasını korumak için)
      const dataToCache = {
        customerStats,
        revenueStats,
        lastYearStatsTemp,
        monthlyTrendData,
        monthlyCustomerTrendData,
        monthlyEarningsTrendData,
        monthlyNetEarningsTrendData,
        deliveryData
      };
      const updateTime = new Date();
      localStorage.setItem('dashboard_stats_cache', JSON.stringify(dataToCache));
      localStorage.setItem('dashboard_stats_time', updateTime.getTime().toString());
      setStatsLastUpdated(updateTime);

      // Yazılımcı filtresi yoksa bu güncel veriyi tek bir write ile stats/dashboard belgesine de yaz (Materialized View Güncelleme)
      if (!isFilteredView) {
        try {
          const statsDocRef = doc(db, 'stats', 'dashboard');
          await setDoc(statsDocRef, {
            ...dataToCache,
            updatedAt: updateTime.getTime()
          });
        } catch (writeErr) {
          console.error('Birleştirilmiş istatistik belgesi güncellenemedi:', writeErr);
        }
      }
    } catch (error) {
      console.error('İstatistikler yüklenirken hata:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [isYazilimci, user?.uid]);

  const handleCustomerChange = useCallback(() => {
    localStorage.removeItem('dashboard_stats_cache');
    localStorage.removeItem('dashboard_stats_time');
    setStatsLastUpdated(null);
    fetchAllStats(true); // Canlı verileri tekrar hesapla
  }, [fetchAllStats]);

  // İstatistikleri yükle
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchAllStats();
    }
  }, [fetchAllStats, activeTab]);

  // Aktif sekme değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);



  // Firma bilgilerini Firebase'den çek
  const fetchCompanies = useCallback(async () => {
    try {
      setIsLoadingCompanies(true);
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // İsme göre sırala
      companiesData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCompanies(companiesData);
    } catch (error) {
      console.error('Firma bilgileri yüklenirken hata:', error);
      setCompanyError('Firma bilgileri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoadingCompanies(false);
    }
  }, []);

  // Firma bilgileri modalı açıldığında verileri çek
  useEffect(() => {
    if (showCompanyInfo) {
      fetchCompanies();
    }
  }, [showCompanyInfo, fetchCompanies]);

  // Firma ekle veya güncelle
  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setCompanyError(null);
    setCompanySuccess(false);

    // Validasyonlar
    if (!companyForm.name || !companyForm.phone) {
      setCompanyError('Lütfen firma adı ve telefon numarasını girin.');
      return;
    }

    // Telefon numarası validasyonu (sadece rakam ve + işareti)
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    if (!phoneRegex.test(companyForm.phone.replace(/\s/g, ''))) {
      setCompanyError('Geçerli bir telefon numarası girin (örn: 905321234567 veya +905321234567)');
      return;
    }

    try {
      const phoneNumber = companyForm.phone.replace(/\s/g, ''); // Boşlukları temizle

      if (editingCompany) {
        // Güncelleme
        await updateDoc(doc(db, 'companies', editingCompany.id), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          updatedAt: new Date()
        });
        setCompanySuccess('Firma bilgileri başarıyla güncellendi!');
      } else {
        // Yeni ekleme
        // Aynı isimde firma var mı kontrol et
        const existingCompany = companies.find(c =>
          c.name.toLowerCase().trim() === companyForm.name.toLowerCase().trim()
        );
        if (existingCompany) {
          setCompanyError('Bu isimde bir firma zaten mevcut.');
          return;
        }

        await addDoc(collection(db, 'companies'), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: user?.uid || null
        });
        setCompanySuccess('Firma başarıyla eklendi!');
      }

      // Formu temizle
      setCompanyForm({ name: '', phone: '' });
      setEditingCompany(null);

      // Listeyi yeniden yükle
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma kaydedilirken hata:', error);
      let errorMessage = 'Firma kaydedilirken bir hata oluştu.';
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        errorMessage = 'Firma ekleme yetkiniz bulunmamaktadır. Lütfen yöneticinizle iletişime geçin.';
      } else if (error.message) {
        errorMessage = `Firma kaydedilirken bir hata oluştu: ${error.message}`;
      }
      setCompanyError(errorMessage);
    }
  };

  // Firma düzenle
  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      phone: company.phone || ''
    });
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // Firma silme modal'ını aç
  const handleDeleteCompanyClick = (company) => {
    setDeleteCompanyModal(company);
  };

  // Firma silme modal'ını kapat
  const handleCloseDeleteCompanyModal = () => {
    setDeleteCompanyModal(null);
  };

  // Firma sil
  const handleDeleteCompany = async () => {
    if (!deleteCompanyModal) return;

    try {
      await deleteDoc(doc(db, 'companies', deleteCompanyModal.id));
      setCompanySuccess('Firma başarıyla silindi!');
      setDeleteCompanyModal(null);
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma silinirken hata:', error);
      setCompanyError('Firma silinirken bir hata oluştu: ' + error.message);
      setDeleteCompanyModal(null);
    }
  };

  // Firma formunu iptal et
  const handleCancelCompany = () => {
    setCompanyForm({ name: '', phone: '' });
    setEditingCompany(null);
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI ========

  // Kullanıcıları getir
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;

    setIsLoadingUsers(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(user => !user.disabled); // Disabled kullanıcıları filtrele
      setUsers(usersData);
    } catch (error) {
      console.error('Kullanıcılar alınırken hata:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [isAdmin]);

  // Kullanıcıları yükle (admin ise)
  useEffect(() => {
    if (isAdmin && activeTab === 'users') {
      fetchUsers();
    }
  }, [isAdmin, activeTab, fetchUsers]);

  // Yeni kullanıcı oluştur
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserFormError(null);

    if (!newUserForm.email || !newUserForm.password || !newUserForm.displayName) {
      setUserFormError('Tüm alanları doldurun');
      return;
    }

    if (newUserForm.password.length < 6) {
      setUserFormError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    if (newUserForm.role === ROLES.YAZILIMCI && newUserForm.email.toLowerCase() !== YAZILIMCI_EMAIL) {
      setUserFormError(`Yazılımcı rolü yalnızca ${YAZILIMCI_EMAIL} adresi için oluşturulabilir.`);
      return;
    }

    try {
      // Firebase Auth'da kullanıcı oluştur - Secondary auth instance kullanıyoruz
      // Ana kullanıcının oturumunu kapatmadan yeni kullanıcı oluşturmak için
      const { initializeApp, getApps, deleteApp } = await import('firebase/app');
      const { getAuth, createUserWithEmailAndPassword: createUser } = await import('firebase/auth');

      // Mevcut Secondary app varsa sil
      const apps = getApps();
      const existingSecondary = apps.find(app => app.name === 'Secondary');
      if (existingSecondary) {
        await deleteApp(existingSecondary);
      }

      // Geçici bir Firebase app instance oluştur (constants'dan config al)
      const secondaryApp = initializeApp(FIREBASE_CONFIG, 'Secondary');
      const secondaryAuth = getAuth(secondaryApp);

      try {
        const userCredential = await createUser(secondaryAuth, newUserForm.email, newUserForm.password);
        const newUser = userCredential.user;

        // Firestore'da kullanıcı kaydı oluştur
        await setDoc(doc(db, 'users', newUser.uid), {
          email: newUserForm.email,
          displayName: newUserForm.displayName,
          role: newUserForm.role,
          createdAt: new Date(),
          createdBy: user.uid
        });

        // Secondary app'i temizle
        await deleteApp(secondaryApp);

        // Formu sıfırla ve modalı kapat
        setNewUserForm({
          email: '',
          password: '',
          displayName: '',
          role: ROLES.MONTAJCI
        });
        setShowAddUserModal(false);

        // Kullanıcı listesini yenile
        fetchUsers();

        showNotificationModal('success', 'Başarılı', `${newUserForm.displayName} kullanıcısı başarıyla oluşturuldu.`);
      } catch (error) {
        await deleteApp(secondaryApp);
        throw error;
      }
    } catch (error) {
      console.error('Kullanıcı oluşturulurken hata:', error);
      if (error.code === 'auth/email-already-in-use') {
        setUserFormError('Bu e-posta adresi zaten kullanılıyor');
      } else if (error.code === 'auth/invalid-email') {
        setUserFormError('Geçersiz e-posta adresi');
      } else if (error.code === 'auth/weak-password') {
        setUserFormError('Şifre çok zayıf');
      } else {
        setUserFormError('Kullanıcı oluşturulurken bir hata oluştu');
      }
    }
  };

  // Kullanıcı rolünü güncelle
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date(),
        updatedBy: user.uid
      });

      // Local state güncelle
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, role: newRole } : u
      ));
      setEditingUserRole(null);

      showNotificationModal('success', 'Başarılı', 'Kullanıcı rolü güncellendi');
    } catch (error) {
      console.error('Rol güncellenirken hata:', error);
      showNotificationModal('error', 'Hata', 'Rol güncellenirken bir hata oluştu');
    }
  };

  // Kullanıcı sil - Kullanıcıyı disabled olarak işaretle (Firestore'dan silme, sadece devre dışı bırak)
  const handleDeleteUser = async (userId) => {
    try {
      // Önce kullanıcı bilgilerini al
      const userToDelete = users.find(u => u.id === userId);
      if (!userToDelete) {
        showNotificationModal('error', 'Hata', 'Kullanıcı bulunamadı');
        return;
      }

      // Firebase Auth'dan kullanıcıyı silmek için Cloud Functions endpoint'ini dene
      try {
        const functions = await import('firebase/functions');
        const { getFunctions, httpsCallable } = functions;
        const funcs = getFunctions();
        const deleteUserFunction = httpsCallable(funcs, 'deleteUser');

        // Cloud Functions ile Firebase Auth'dan da sil
        await deleteUserFunction({ userId: userId, email: userToDelete.email });

        // Başarılı olursa Firestore'dan da sil
        await deleteDoc(doc(db, 'users', userId));
      } catch (functionsError) {
        // Cloud Functions yoksa veya hata varsa, sadece disabled olarak işaretle
        // Firestore'dan SİLME, sadece disabled yap
        await updateDoc(doc(db, 'users', userId), {
          disabled: true,
          disabledAt: new Date(),
          disabledBy: user.uid
        });
      }

      // Kullanıcı listesini yeniden yükle
      await fetchUsers();

      setDeleteUserModal(null);
      showNotificationModal('success', 'Başarılı', 'Kullanıcı başarıyla devre dışı bırakıldı');
    } catch (error) {
      console.error('Kullanıcı silinirken hata:', error);
      showNotificationModal('error', 'Hata', 'Kullanıcı silinirken bir hata oluştu: ' + error.message);
    }
  };

  // Rol adını Türkçe olarak döndür
  const getRoleName = (role) => {
    const roleNames = {
      [ROLES.ADMIN]: 'Admin',
      [ROLES.SATISCI]: 'Satışcı',
      [ROLES.MONTAJCI]: 'Montajcı',
      [ROLES.YAZILIMCI]: 'Yazılımcı',
      [ROLES.SATIS_DANISMANI]: 'Satış Danışmanı'
    };
    return roleNames[role] || role;
  };

  // Rol badge rengini döndür
  const getRoleBadgeClass = (role) => {
    const classes = {
      [ROLES.ADMIN]: 'role-badge-admin',
      [ROLES.SATISCI]: 'role-badge-satisci',
      [ROLES.MONTAJCI]: 'role-badge-montajci',
      [ROLES.YAZILIMCI]: 'role-badge-yazilimci',
      [ROLES.SATIS_DANISMANI]: 'role-badge-satisdanismani'
    };
    return classes[role] || '';
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI SONU ========

  const handleLogout = async () => {
    try {
      // Firebase'den çıkış yap
      await signOut(auth);
      // Firebase otomatik olarak oturumu temizler
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Çıkış hatası:', error);
      // Hata olsa bile ana sayfaya yönlendir
      navigate(ROUTES.LOGIN);
    }
  };


  // Backup fonksiyonu
  const handleExport = async () => {
    try {
      setIsExporting(true);
      const result = await exportData(userRole, user?.uid);
      showNotificationModal(
        'success',
        'Yedekleme Başarılı',
        'Verileriniz başarıyla yedeklendi.',
        {
          customers: result.customers,
          orders: result.orders
        }
      );
    } catch (error) {
      showNotificationModal('error', 'Yedekleme Hatası', error.message);
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import modal'ını aç
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportFile(null);
  };

  // Dosya seçimi
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        validateBackupFile(file);
        setImportFile(file);
      } catch (error) {
        showNotificationModal('error', 'Dosya Hatası', error.message);
        event.target.value = '';
      }
    }
  };

  // Import işlemi
  const handleImport = async () => {
    if (!importFile) {
      showNotificationModal('warning', 'Dosya Seçilmedi', 'Lütfen bir yedek dosyası seçin.');
      return;
    }

    try {
      setIsImporting(true);
      const result = await importData(importFile);

      // Modal'ı kapat
      setShowImportModal(false);
      setImportFile(null);

      // İstatistikleri yeniden yükle
      fetchAllStats();

      // Başarı modal'ını göster
      showNotificationModal(
        'success',
        'Yükleme Başarılı',
        'Yedek dosyası başarıyla yüklendi.',
        {
          backupCustomers: result.totalCustomers,
          backupOrders: result.totalOrders,
          addedCustomers: result.addedCustomers,
          updatedCustomers: result.updatedCustomers,
          addedOrders: result.addedOrders,
          updatedOrders: result.updatedOrders
        }
      );
    } catch (error) {
      showNotificationModal('error', 'Yükleme Hatası', error.message);
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // Modal'ı kapat
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
  };

  //Şifre değiştirme fonksiyonu
  const handleChangePasswordRequest = (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validasyonlar
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Lütfen tüm alanları doldurun.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Yeni şifre ve şifre onayı eşleşmiyor.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Yeni şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('Yeni şifre eski şifre ile aynı olamaz.');
      return;
    }

    setShowPasswordConfirmModal(true);
  };

  const confirmPasswordChange = async () => {
    setShowPasswordConfirmModal(false);
    try {
      setIsChangingPassword(true);

      //Mevcut şifreyi doğrula
      const user = auth.currentUser;
      if (!user || !user.email) {
        setPasswordError("Kullanıcı bilgisi bulunamadı.");
        setIsChangingPassword(false);
        return;
      }

      // Mevcut şifreyi doğrula (ayrı try-catch ile)
      try {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
      } catch (authError) {
        console.error('Şifre doğrulama hatası:', authError);

        // Mevcut şifre ile ilgili hatalar
        if (authError.code === 'auth/wrong-password' ||
          authError.code === 'auth/invalid-credential' ||
          authError.code === 'auth/invalid-email' ||
          authError.message?.includes('password') ||
          authError.message?.includes('credential')) {
          setPasswordError('Mevcut şifre hatalı. Lütfen doğru şifreyi girin.');
        } else if (authError.code === 'auth/user-mismatch') {
          setPasswordError('Kullanıcı bilgisi eşleşmiyor. Lütfen tekrar giriş yapın.');
        } else if (authError.code === 'auth/requires-recent-login') {
          setPasswordError('Güvenlik nedeniyle lütfen tekrar giriş yapın ve şifre değiştirmeyi tekrar deneyin.');
        } else {
          setPasswordError('Mevcut şifre doğrulanamadı. Lütfen şifrenizi kontrol edin.');
        }
        setIsChangingPassword(false);
        return;
      }

      // Şifreyi güncelle (ayrı try-catch ile)
      try {
        await updatePassword(user, newPassword);
      } catch (updateError) {
        console.error('Şifre güncelleme hatası:', updateError);

        if (updateError.code === 'auth/weak-password') {
          setPasswordError('Yeni şifre çok zayıf. Daha güçlü bir şifre seçin (en az 6 karakter, büyük/küçük harf, rakam önerilir).');
        } else if (updateError.code === 'auth/requires-recent-login') {
          setPasswordError('Güvenlik nedeniyle lütfen tekrar giriş yapın ve şifre değiştirmeyi tekrar deneyin.');
        } else {
          setPasswordError('Şifre güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
        }
        setIsChangingPassword(false);
        return;
      }

      // Başarılı
      setPasswordSuccess('Şifreniz başarıyla değiştirildi!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // 3 saniye sonra formu kapat
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      setPasswordError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Kullanıcı Yönetimi Render
  const renderUserManagement = () => {
    return (
      <div className="user-management-content">
        <div className="section-header">
          <h2><i className="fas fa-users-cog"></i> Kullanıcı Yönetimi</h2>
          <button
            className="btn-add-user"
            onClick={() => setShowAddUserModal(true)}
          >
            <i className="fas fa-user-plus"></i>
            Yeni Kullanıcı
          </button>
        </div>

        {isLoadingUsers ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Kullanıcılar yükleniyor...</span>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Kullanıcı Adı</th>
                  <th>E-posta</th>
                  <th>Rol</th>
                  <th>Oluşturulma Tarihi</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.displayName || '-'}</td>
                    <td>{u.email}</td>
                    <td>
                      {editingUserRole === u.id ? (
                        <select
                          value={u.role}
                          onChange={(e) => handleUpdateUserRole(u.id, e.target.value)}
                          onBlur={() => setEditingUserRole(null)}
                          autoFocus
                        >
                          <option value={ROLES.ADMIN}>Admin</option>
                          <option value={ROLES.SATISCI}>Satışcı</option>
                          <option value={ROLES.MONTAJCI}>Montajcı</option>
                          <option value={ROLES.YAZILIMCI}>Yazılımcı</option>
                          <option value={ROLES.SATIS_DANISMANI}>Satış Danışmanı</option>
                        </select>
                      ) : (
                        <span
                          className={`role-badge ${getRoleBadgeClass(u.role)}`}
                          onClick={() => u.id !== user?.uid && setEditingUserRole(u.id)}
                          style={{ cursor: u.id !== user?.uid ? 'pointer' : 'default' }}
                          title={u.id !== user?.uid ? 'Rolü değiştirmek için tıklayın' : 'Kendi rolünüzü değiştiremezsiniz'}
                        >
                          {getRoleName(u.role)}
                        </span>
                      )}
                    </td>
                    <td>
                      {u.createdAt?.toDate?.()
                        ? new Date(u.createdAt.toDate()).toLocaleDateString('tr-TR')
                        : '-'}
                    </td>
                    <td>
                      {u.id !== user?.uid ? (
                        <button
                          className="btn-delete-user"
                          onClick={() => setDeleteUserModal(u)}
                          title="Kullanıcıyı Sil"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      ) : (
                        <span className="current-user-badge">Siz</span>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      Henüz kullanıcı bulunmamaktadır.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="role-info-section">
          <h3><i className="fas fa-info-circle"></i> Rol Yetkileri</h3>
          <div className="role-info-grid">
            <div className="role-info-card">
              <span className="role-badge role-badge-admin">Admin</span>
              <p>Tüm sisteme tam erişim. Kullanıcı oluşturma ve silme yetkisi.</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-montajci">Montajcı</span>
              <p>Sadece montaj durumundaki siparişleri görme ve teslim edildi olarak işaretleme.</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-yazilimci">Yazılımcı</span>
              <p>Tüm özelliklere erişim, ancak sadece kendi oluşturduğu verileri görme.</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-satisdanismani">Satış Danışmanı</span>
              <p>Müşteri ve Ürün Listesine tam erişim. Tüm verileri görebilir. Ana Sayfa ve Kullanıcılar bölümüne erişemez.</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'customers':
        return <CustomerList onCustomerChange={handleCustomerChange} userRole={userRole} userId={user?.uid} />;
      case 'products':
        if (isMontajci) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return <ProductList />;
      case 'users':
        if (!isAdmin) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return renderUserManagement();
      /* GEÇİCİ DEVRE DIŞI - Hesaplamalar sekmesi kaldırıldı
      case 'accounting':
        if (!isAdmin) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return <AccountingReport />;
      */
      case 'dashboard':
      default:
        // Satışcı dashboard'a erişemez
        if (!permissions?.canAccessDashboard) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Dashboard'a erişim yetkiniz bulunmamaktadır.</p>
              <p>Müşteri Listesi sekmesinden işlemlerinizi yapabilirsiniz.</p>
            </div>
          );
        }
        return (
          <div className="dashboard-content">
            <div className="welcome-card">
              <h2>Hoş Geldiniz!</h2>
              <p>Müşteri listesi yönetim paneline başarıyla giriş yaptınız.</p>
              <p>Burada müşteri listesi işlemlerinizi gerçekleştirebilirsiniz.</p>
              {user?.email && (
                <div className="user-info">
                  <p><strong>Giriş Yapan:</strong> {user.email}</p>
                  {userRole && (
                    <p><strong>Rol:</strong> <span className={`role-badge ${getRoleBadgeClass(userRole)}`}>{getRoleName(userRole)}</span></p>
                  )}
                </div>
              )}
            </div>

            <div className="cache-info-banner">
              <div className="cache-info-text">
                <i className="fas fa-history"></i>
                <span>
                  İstatistikler en son <strong>{statsLastUpdated ? statsLastUpdated.toLocaleTimeString('tr-TR') : '...'}</strong> saatinde güncellendi.
                </span>
              </div>
              <button 
                className="refresh-stats-btn" 
                onClick={() => fetchAllStats(true)} 
                disabled={isLoadingStats}
              >
                {isLoadingStats ? (
                  <><i className="fas fa-spinner fa-spin"></i> Güncelleniyor...</>
                ) : (
                  <><i className="fas fa-sync-alt"></i> Verileri Yenile</>
                )}
              </button>
            </div>

            {/* Teslimat Hatırlatıcısı */}
            {(deliveryReminders.overdue.length > 0 ||
              deliveryReminders.today.length > 0 ||
              deliveryReminders.tomorrow.length > 0 ||
              deliveryReminders.thisWeek.length > 0) && (
                <div className="delivery-reminders-section">
                  <h3><i className="fas fa-bell"></i> Teslimat Hatırlatıcısı</h3>

                  <div className="delivery-reminders-grid">
                    {/* Geciken Teslimatlar */}
                    {deliveryReminders.overdue.length > 0 && (
                      <div className="reminder-card reminder-overdue">
                        <div className="reminder-header">
                          <i className="fas fa-exclamation-triangle"></i>
                          <span>Geciken Teslimatlar</span>
                          <span className="reminder-count">{deliveryReminders.overdue.length}</span>
                        </div>
                        <div className="reminder-list">
                          {deliveryReminders.overdue.slice(0, 5).map(customer => (
                            <div key={customer.id} className="reminder-item">
                              <div className="reminder-customer-name">{customer.name}</div>
                              <div className="reminder-details">
                                <span className="reminder-days">{customer.daysOverdue} gün gecikti</span>
                                {customer.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {customer.phone}</span>}
                              </div>
                            </div>
                          ))}
                          {deliveryReminders.overdue.length > 5 && (
                            <div className="reminder-more">+{deliveryReminders.overdue.length - 5} daha...</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Bugün Teslim Edilecek */}
                    {deliveryReminders.today.length > 0 && (
                      <div className="reminder-card reminder-today">
                        <div className="reminder-header">
                          <i className="fas fa-clock"></i>
                          <span>Bugün Teslim</span>
                          <span className="reminder-count">{deliveryReminders.today.length}</span>
                        </div>
                        <div className="reminder-list">
                          {deliveryReminders.today.slice(0, 5).map(customer => (
                            <div key={customer.id} className="reminder-item">
                              <div className="reminder-customer-name">{customer.name}</div>
                              <div className="reminder-details">
                                {customer.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {customer.phone}</span>}
                                {customer.address && <span className="reminder-address"><i className="fas fa-map-marker-alt"></i> {customer.address.substring(0, 30)}...</span>}
                              </div>
                            </div>
                          ))}
                          {deliveryReminders.today.length > 5 && (
                            <div className="reminder-more">+{deliveryReminders.today.length - 5} daha...</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Yarın Teslim Edilecek */}
                    {deliveryReminders.tomorrow.length > 0 && (
                      <div className="reminder-card reminder-tomorrow">
                        <div className="reminder-header">
                          <i className="fas fa-calendar-day"></i>
                          <span>Yarın Teslim</span>
                          <span className="reminder-count">{deliveryReminders.tomorrow.length}</span>
                        </div>
                        <div className="reminder-list">
                          {deliveryReminders.tomorrow.slice(0, 5).map(customer => (
                            <div key={customer.id} className="reminder-item">
                              <div className="reminder-customer-name">{customer.name}</div>
                              <div className="reminder-details">
                                {customer.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {customer.phone}</span>}
                              </div>
                            </div>
                          ))}
                          {deliveryReminders.tomorrow.length > 5 && (
                            <div className="reminder-more">+{deliveryReminders.tomorrow.length - 5} daha...</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Bu Hafta Teslim Edilecek */}
                    {deliveryReminders.thisWeek.length > 0 && (
                      <div className="reminder-card reminder-week">
                        <div className="reminder-header">
                          <i className="fas fa-calendar-week"></i>
                          <span>Bu Hafta</span>
                          <span className="reminder-count">{deliveryReminders.thisWeek.length}</span>
                        </div>
                        <div className="reminder-list">
                          {deliveryReminders.thisWeek.slice(0, 5).map(customer => (
                            <div key={customer.id} className="reminder-item">
                              <div className="reminder-customer-name">{customer.name}</div>
                              <div className="reminder-details">
                                <span className="reminder-days">{customer.daysLeft} gün kaldı</span>
                                {customer.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {customer.phone}</span>}
                              </div>
                            </div>
                          ))}
                          {deliveryReminders.thisWeek.length > 5 && (
                            <div className="reminder-more">+{deliveryReminders.thisWeek.length - 5} daha...</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Müşteri İstatistikleri */}
            <div className="stats-section">
              <h3>Müşteri İstatistikleri</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Toplam Müşteri</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.total}
                    </div>
                    <small>Tüm kayıtlı müşteriler</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-user-check"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Aktif Müşteri</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.active}
                    </div>
                    <small>Yeni sipariş durumunda</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-day"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bugün</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.today}
                    </div>
                    <small>Bugün sipariş oluşturan</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-week"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bu Hafta</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.thisWeek}
                    </div>
                    <small>Son 7 günde sipariş oluşturan</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bu Ay</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.thisMonth}
                    </div>
                    <small>Bu ay sipariş oluşturan</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bu Yıl</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.thisYear}
                    </div>
                    <small>Bu yıl sipariş oluşturan</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Aylık Müşteri Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>Aylık Müşteri Trendi ({new Date().getFullYear()})</h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthlyCustomerTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#1a1a1a" />
                    <YAxis allowDecimals={false} stroke="#1a1a1a" />
                    <Tooltip formatter={(value) => [`${value}`, 'Müşteri']} />
                    <Line type="monotone" dataKey="count" name="Müşteri" stroke="#1a1a1a" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Aylık Sipariş Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>Aylık Sipariş Trendi ({new Date().getFullYear()})</h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthlyOrderTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#1a1a1a" />
                    <YAxis allowDecimals={false} stroke="#1a1a1a" />
                    <Tooltip formatter={(value) => [`${value}`, 'Sipariş']} />
                    <Line type="monotone" dataKey="count" name="Sipariş" stroke="#1a1a1a" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Aylık Kazanç Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>Aylık Kazanç Trendi ({new Date().getFullYear()})</h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthlyEarningsTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#1a1a1a" />
                    <YAxis
                      stroke="#1a1a1a"
                      tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}K` : value}
                    />
                    <Tooltip
                      formatter={(value) => [`${formatPrice(value)} ₺`, 'Kazanç']}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="earnings"
                      name="Kazanç"
                      stroke="#2e7d32"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#2e7d32' }}
                      activeDot={{ r: 6, fill: '#1b5e20' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Aylık Net Kazanç Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>Aylık Net Kazanç Trendi ({new Date().getFullYear()})</h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthlyNetEarningsTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#1a1a1a" />
                    <YAxis
                      stroke="#1a1a1a"
                      tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}K` : value}
                    />
                    <Tooltip
                      formatter={(value) => [`${formatPrice(value)} ₺`, 'Net Kazanç']}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="netEarnings"
                      name="Net Kazanç"
                      stroke="#1e88e5"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#1e88e5' }}
                      activeDot={{ r: 6, fill: '#1565c0' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Gelir İstatistikleri */}
            <div className="stats-section revenue-stats">
              <h3>Gelir İstatistikleri</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-coins"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Beklenen Gelir</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.expectedTotal)} ₺`}
                    </div>
                    <small>Tüm siparişler toplamı</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-wallet"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Toplam Gelir</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.total)} ₺`}
                    </div>
                    <small>Alınan ödemeler</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: 'rgba(30, 136, 229, 0.1)', color: '#1e88e5' }}>
                    <i className="fas fa-chart-pie"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Toplam Net Gelir</h3>
                    <div className="stat-number" style={{ color: '#1e88e5' }}>
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.netTotal)} ₺`}
                    </div>
                    <small>Alınan Ödeme - Toplam Maliyet</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}>
                    <i className="fas fa-hand-holding-usd"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Potansiyel Net Kazanç</h3>
                    <div className="stat-number" style={{ color: '#4caf50' }}>
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.expectedNet)} ₺`}
                    </div>
                    <small>Toplam Satış - Toplam Maliyet</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-hourglass-half"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Kalan Ödemeler</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.remainingTotal)} ₺`}
                    </div>
                    <small>Tahsil edilecek</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-day"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bugün</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.today)} ₺`}
                    </div>
                    <small>Bugünkü gelir</small>
                    <div className="stat-net-badge" title="Bugünkü Alınan Ödeme - Maliyet">
                      Kâr: {isLoadingStats ? '...' : `${formatPrice(revenueStats.todayNet)} ₺`}
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-week"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bu Hafta</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.thisWeek)} ₺`}
                    </div>
                    <small>Son 7 gün</small>
                    <div className="stat-net-badge" title="Haftalık Alınan Ödeme - Maliyet">
                      Kâr: {isLoadingStats ? '...' : `${formatPrice(revenueStats.thisWeekNet)} ₺`}
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bu Ay</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.thisMonth)} ₺`}
                    </div>
                    <small>Bu ayki gelir</small>
                    <div className="stat-net-badge" title="Aylık Alınan Ödeme - Maliyet">
                      Kâr: {isLoadingStats ? '...' : `${formatPrice(revenueStats.thisMonthNet)} ₺`}
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bu Yıl</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.thisYear)} ₺`}
                    </div>
                    <small>Bu yılki gelir</small>
                    <div className="stat-net-badge" title="Yıllık Alınan Ödeme - Maliyet">
                      Kâr: {isLoadingStats ? '...' : `${formatPrice(revenueStats.thisYearNet)} ₺`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ödeme Tipine Göre Günlük Gelirler */}
              <h4 className="payment-type-title"><i className="fas fa-receipt"></i> Ödeme Tipine Göre</h4>
              <div className="stats-grid payment-type-stats">
                <div className="stat-card payment-cash">
                  <div className="stat-icon">
                    <i className="fas fa-money-bill-wave"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bugün (Nakit)</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.todayCash)} ₺`}
                    </div>
                    <small>Elden nakit ödemeler</small>
                  </div>
                </div>
                <div className="stat-card payment-iban">
                  <div className="stat-icon">
                    <i className="fas fa-university"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Bugün (IBAN)</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.todayIban)} ₺`}
                    </div>
                    <small>Banka transferi</small>
                  </div>
                </div>
                <div className="stat-card payment-credit-card">
                  <div className="stat-icon">
                    <i className="fas fa-credit-card"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Dün (Kredi Kartı)</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.yesterdayCreditCard)} ₺`}
                    </div>
                    <small>Bir gün gecikmeyle yansır</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Geçen Yıl Karşılaştırması */}
            <div className="stats-section">
              <h3>Geçen Yıl Karşılaştırması ({new Date().getFullYear() - 1})</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-history"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Geçen Yıl Bugün</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.today}
                    </div>
                    <small>Aynı gün müşteri sayısı</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-history"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Geçen Yıl Bu Hafta</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.thisWeek}
                    </div>
                    <small>Aynı hafta müşteri sayısı</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-history"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Geçen Yıl Bu Ay</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.thisMonth}
                    </div>
                    <small>Aynı ay müşteri sayısı</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-chart-bar"></i>
                  </div>
                  <div className="stat-content">
                    <h3>Geçen Yıl Toplam</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.total}
                    </div>
                    <small>Yıl boyunca toplam</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Backup/Restore Bölümü */}
            <div className="backup-section">
              <h3>Veri Yönetimi</h3>
              <div className="backup-grid">
                <div className="backup-card">
                  <div className="backup-icon">📥</div>
                  <h4>Yedek Al</h4>
                  <p>Tüm müşteri ve sipariş verilerinizi JSON formatında yedekleyin</p>
                  <button
                    onClick={handleExport}
                    disabled={isExporting || isLoadingStats}
                    className="backup-btn export-btn"
                  >
                    {isExporting ? (
                      <span>
                        <div className="btn-spinner"></div>
                        Yedekleniyor...
                      </span>
                    ) : (
                      <>
                        <i className="fas fa-download"></i>
                        Yedek Al
                      </>
                    )}
                  </button>
                </div>

                <div className="backup-card">
                  <div className="backup-icon">📤</div>
                  <h4>Yedek Yükle</h4>
                  <p>Daha önce aldığınız backup dosyasını geri yükleyin</p>
                  <button
                    onClick={handleImportClick}
                    disabled={isImporting || isLoadingStats}
                    className="backup-btn import-btn"
                  >
                    <i className="fas fa-upload"></i>
                    Yedek Yükle
                  </button>
                </div>
              </div>

              <div className="backup-info">
                <i className="fas fa-info-circle"></i>
                <p>
                  <strong>Önemli:</strong> Yedek yükleme işlemi mevcut verilerinizi silmez.
                  Aynı ID'ye sahip veriler güncellenir, yeni veriler eklenir.
                </p>
              </div>
            </div>

            {/* Firma Bilgileri Bölümü */}
            <div className="company-info-section">
              <div className="company-info-header">
                <h3>
                  <i className="fas fa-building"></i>
                  Firma Bilgileri
                </h3>
                <button
                  className="btn-toggle-company-info"
                  onClick={() => {
                    setShowCompanyInfo(!showCompanyInfo);
                    setCompanyError(null);
                    setCompanySuccess(false);
                    if (!showCompanyInfo) {
                      handleCancelCompany();
                    }
                  }}
                >
                  {showCompanyInfo ? (
                    <>
                      <i className="fas fa-chevron-up"></i>
                      Gizle
                    </>
                  ) : (
                    <>
                      <i className="fas fa-chevron-down"></i>
                      Göster
                    </>
                  )}
                </button>
              </div>

              {showCompanyInfo && (
                <div className="company-info-content">
                  {companyError && (
                    <div className="company-message error">
                      <i className="fas fa-exclamation-circle"></i>
                      {companyError}
                    </div>
                  )}

                  {companySuccess && (
                    <div className="company-message success">
                      <i className="fas fa-check-circle"></i>
                      {companySuccess}
                    </div>
                  )}

                  <form onSubmit={handleSaveCompany} className="company-form">
                    <div className="form-group">
                      <label htmlFor="companyName">
                        <i className="fas fa-building"></i>
                        Firma Adı *
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        value={companyForm.name}
                        onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                        placeholder="Firma adını girin"
                        required
                        disabled={isLoadingCompanies}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="companyPhone">
                        <i className="fas fa-phone"></i>
                        Telefon Numarası *
                      </label>
                      <input
                        type="text"
                        id="companyPhone"
                        value={companyForm.phone}
                        onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                        placeholder="905321234567 veya +905321234567"
                        required
                        disabled={isLoadingCompanies}
                      />
                      <small>Ülke kodu ile birlikte girin (örn: 905321234567)</small>
                    </div>

                    <div className="company-form-actions">
                      <button
                        type="submit"
                        className="btn-save-company"
                        disabled={isLoadingCompanies}
                      >
                        {isLoadingCompanies ? (
                          <>
                            <div className="btn-spinner"></div>
                            Kaydediliyor...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            {editingCompany ? 'Güncelle' : 'Ekle'}
                          </>
                        )}
                      </button>
                      {editingCompany && (
                        <button
                          type="button"
                          className="btn-cancel-company"
                          onClick={handleCancelCompany}
                          disabled={isLoadingCompanies}
                        >
                          İptal
                        </button>
                      )}
                    </div>
                  </form>

                  {/* Firma Listesi */}
                  <div className="companies-list">
                    <h4>Kayıtlı Firmalar</h4>
                    {isLoadingCompanies ? (
                      <div className="loading-companies">
                        <div className="stat-spinner"></div>
                        <span>Yükleniyor...</span>
                      </div>
                    ) : companies.length === 0 ? (
                      <div className="no-companies">
                        <p>Henüz firma eklenmemiş.</p>
                      </div>
                    ) : (
                      <div className="companies-table-wrapper">
                        <table className="companies-table">
                          <thead>
                            <tr>
                              <th>Firma Adı</th>
                              <th>Telefon</th>
                              <th>İşlemler</th>
                            </tr>
                          </thead>
                          <tbody>
                            {companies.map((company) => (
                              <tr key={company.id}>
                                <td>{company.name}</td>
                                <td>{company.phone}</td>
                                <td>
                                  <div className="company-actions">
                                    <button
                                      onClick={() => handleEditCompany(company)}
                                      className="edit-company-btn"
                                      disabled={isLoadingCompanies}
                                      title="Düzenle"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteCompanyClick(company)}
                                      className="delete-company-btn"
                                      disabled={isLoadingCompanies}
                                      title="Sil"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="company-info-info">
                    <i className="fas fa-info-circle"></i>
                    <div>
                      <strong>Bilgi:</strong>
                      <ul>
                        <li>Eklediğiniz firmalar WhatsApp mesaj gönderme bölümünde görünecektir.</li>
                        <li>Telefon numarasını ülke kodu ile birlikte girin (örn: 905321234567).</li>
                        <li>Firma adı benzersiz olmalıdır.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Şifre Değiştirme Bölümü */}
            <div className="password-change-section">
              <div className="password-change-header">
                <h3>
                  <i className="fas fa-key"></i>
                  Şifre Değiştir
                </h3>
                <button
                  className="btn-toggle-password-change"
                  onClick={() => {
                    setShowPasswordChange(!showPasswordChange);
                    setPasswordError('');
                    setPasswordSuccess('');
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                >
                  {showPasswordChange ? (
                    <>
                      <i className="fas fa-chevron-up"></i>
                      Gizle
                    </>
                  ) : (
                    <>
                      <i className="fas fa-chevron-down"></i>
                      Göster
                    </>
                  )}
                </button>
              </div>

              {showPasswordChange && (
                <div className="password-change-content">
                  {passwordError && (
                    <div className="password-message error">
                      <i className="fas fa-exclamation-circle"></i>
                      {passwordError}
                    </div>
                  )}

                  {passwordSuccess && (
                    <div className="password-message success">
                      <i className="fas fa-check-circle"></i>
                      {passwordSuccess}
                    </div>
                  )}

                  <form onSubmit={handleChangePasswordRequest} className="password-change-form">
                    <div className="form-group">
                      <label htmlFor="currentPassword">
                        <i className="fas fa-lock"></i>
                        Mevcut Şifre *
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Mevcut şifrenizi girin"
                        required
                        disabled={isChangingPassword}
                        autoComplete="current-password"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="newPassword">
                        <i className="fas fa-key"></i>
                        Yeni Şifre *
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Yeni şifrenizi girin (en az 6 karakter)"
                        required
                        disabled={isChangingPassword}
                        autoComplete="new-password"
                        minLength={6}
                      />
                      <small>En az 6 karakter olmalıdır</small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">
                        <i className="fas fa-key"></i>
                        Yeni Şifre Tekrar *
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Yeni şifrenizi tekrar girin"
                        required
                        disabled={isChangingPassword}
                        autoComplete="new-password"
                        minLength={6}
                      />
                    </div>

                    <div className="password-change-actions">
                      <button
                        type="submit"
                        className="btn-change-password"
                        disabled={isChangingPassword}
                      >
                        {isChangingPassword ? (
                          <>
                            <div className="btn-spinner"></div>
                            Değiştiriliyor...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            Şifreyi Değiştir
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel-password"
                        onClick={() => {
                          setShowPasswordChange(false);
                          setPasswordError('');
                          setPasswordSuccess('');
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        disabled={isChangingPassword}
                      >
                        İptal
                      </button>
                    </div>
                  </form>

                  <div className="password-change-info">
                    <i className="fas fa-info-circle"></i>
                    <div>
                      <strong>Güvenlik Notları:</strong>
                      <ul>
                        <li>Şifreniz en az 6 karakter olmalıdır.</li>
                        <li>Güvenli bir şifre için büyük/küçük harf, rakam ve özel karakter kullanın.</li>
                        <li>Şifre değiştirme işlemi sonrasında <strong>diğer tüm cihazlardaki oturumunuz sonlandırılacaktır.</strong></li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#fcfcfc' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="stat-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px', margin: '0 auto 15px' }}></div>
          <h2 style={{ color: '#555', fontSize: '18px', fontWeight: '500' }}>Yetkiler Kontrol Ediliyor...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-top">
          <h1>Stil Perde - Müşteri ve Sipariş Listesi</h1>
          <div className="header-actions">
            {user?.email && (
              <span className="user-email">{user.email}</span>
            )}
            <button onClick={handleLogout} className="logout-button">
              Çıkış Yap
            </button>
          </div>
        </div>

        <div className="header-bottom">
          <nav className="dashboard-nav">
            {/* Ana Sayfa - Satışcı hariç herkes */}
            {permissions?.canAccessDashboard && (
              <button
                className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
              >
                Ana Sayfa
              </button>
            )}

            {/* Müşteri Listesi - Herkes (Montajcı sadece montaj durumundakiler) */}
            <button
              className={`nav-btn ${activeTab === 'customers' ? 'active' : ''}`}
              onClick={() => setActiveTab('customers')}
            >
              {isMontajci ? 'Montaj Listesi' : 'Müşteri Listesi'}
            </button>

            {/* Kullanıcı Yönetimi - Sadece Admin */}
            {isAdmin && (
              <button
                className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <i className="fas fa-users-cog"></i> Kullanıcılar
              </button>
            )}

            {/* Ürün Listesi - Montajcı hariç herkes */}
            {!isMontajci && (
              <button
                className={`nav-btn ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                <i className="fas fa-tags"></i> Ürün Listesi
              </button>
            )}

            {/* GEÇİCİ DEVRE DIŞI - Hesaplamalar sekmesi kaldırıldı
            {isAdmin && (
              <button
                className={`nav-btn ${activeTab === 'accounting' ? 'active' : ''}`}
                onClick={() => setActiveTab('accounting')}
              >
                <i className="fas fa-calculator"></i> Hesaplamalar
              </button>
            )}
            */}
          </nav>
        </div>
      </header>

      <main className="dashboard-main">
        {renderContent()}
      </main>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <i className="fas fa-store"></i>
            <span>{APP_NAME}</span>
          </div>

          <div className="footer-center">
            <span className="footer-dev-label">Developed by</span>
            <a
              href="https://www.kahramantech.com"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-dev-link"
            >
              <i className="fas fa-code footer-dev-icon"></i>
              <span>KahramanTech</span>
              <i className="fas fa-external-link-alt footer-ext-icon"></i>
            </a>
          </div>

          <div className="footer-right">
            <span className="footer-copyright">© {new Date().getFullYear()} Tüm hakları saklıdır.</span>
            <span className="footer-sep">·</span>
            <span className="version-badge">v{APP_VERSION}</span>
          </div>
        </div>
      </footer>

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content import-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-upload"></i>
                Yedek Dosyası Yükle
              </h3>
              <button onClick={closeImportModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  id="backup-file-input"
                  className="file-input"
                />
                <label htmlFor="backup-file-input" className="file-upload-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>JSON dosyası seçin veya sürükleyin</span>
                </label>

                {importFile && (
                  <div className="selected-file">
                    <i className="fas fa-file-alt"></i>
                    <span>{importFile.name}</span>
                    <small>({(importFile.size / 1024).toFixed(1)} KB)</small>
                  </div>
                )}
              </div>

              <div className="import-warning">
                <i className="fas fa-exclamation-triangle"></i>
                <div>
                  <strong>Dikkat:</strong>
                  <ul>
                    <li>Backup dosyası JSON formatında olmalıdır</li>
                    <li>Mevcut verileriniz korunur, silinmez</li>
                    <li>Aynı ID'ye sahip veriler güncellenir</li>
                    <li>İşlem geri alınamaz, önceden yedek aldığınızdan emin olun</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className="btn-primary import-confirm-btn"
              >
                {isImporting ? (
                  <span>
                    <div className="btn-spinner"></div>
                    Yükleniyor...
                  </span>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    Yedek Yükle
                  </>
                )}
              </button>
              <button
                onClick={closeImportModal}
                disabled={isImporting}
                className="btn-secondary"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.show && (
        <div className="modal-overlay">
          <div className={`modal-content notification-modal ${notificationModal.type}`}>
            <div className="modal-header">
              <h3>
                {notificationModal.type === 'success' && <i className="fas fa-check-circle"></i>}
                {notificationModal.type === 'error' && <i className="fas fa-times-circle"></i>}
                {notificationModal.type === 'warning' && <i className="fas fa-exclamation-triangle"></i>}
                {notificationModal.type === 'info' && <i className="fas fa-info-circle"></i>}
                {notificationModal.title}
              </h3>
              <button onClick={closeNotificationModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="notification-message">{notificationModal.message}</p>

              {/* Export detayları */}
              {notificationModal.details && notificationModal.details.customers !== undefined && !notificationModal.details.backupCustomers && (
                <div className="notification-details">
                  <div className="detail-item">
                    <i className="fas fa-users"></i>
                    <span>{notificationModal.details.customers} müşteri</span>
                  </div>
                  <div className="detail-item">
                    <i className="fas fa-shopping-cart"></i>
                    <span>{notificationModal.details.orders} sipariş</span>
                  </div>
                </div>
              )}

              {/* Import detayları */}
              {notificationModal.details && notificationModal.details.backupCustomers !== undefined && (
                <div className="notification-details import-details">
                  <div className="detail-section">
                    <h4>📊 Yedek Dosyası</h4>
                    <div className="detail-row">
                      <span>{notificationModal.details.backupCustomers} müşteri</span>
                      <span>{notificationModal.details.backupOrders} sipariş</span>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>✅ İşlem Sonucu</h4>
                    <div className="detail-grid">
                      <div className="detail-item success">
                        <span className="detail-label">Yeni Müşteri</span>
                        <span className="detail-value">{notificationModal.details.addedCustomers}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">Güncellenen Müşteri</span>
                        <span className="detail-value">{notificationModal.details.updatedCustomers}</span>
                      </div>
                      <div className="detail-item success">
                        <span className="detail-label">Yeni Sipariş</span>
                        <span className="detail-value">{notificationModal.details.addedOrders}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">Güncellenen Sipariş</span>
                        <span className="detail-value">{notificationModal.details.updatedOrders}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={closeNotificationModal} className="btn-primary">
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firma Silme Onay Modal */}
      {deleteCompanyModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-company-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                Firma Sil
              </h3>
              <button onClick={handleCloseDeleteCompanyModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                <strong>{deleteCompanyModal.name}</strong> adlı firmayı silmek istediğinizden emin misiniz?
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                Bu işlem geri alınamaz!
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleDeleteCompany}
                className="btn-primary delete-confirm-btn"
                disabled={isLoadingCompanies}
              >
                {isLoadingCompanies ? (
                  <>
                    <div className="btn-spinner"></div>
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i>
                    Evet, Sil
                  </>
                )}
              </button>
              <button
                onClick={handleCloseDeleteCompanyModal}
                className="btn-secondary"
                disabled={isLoadingCompanies}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Kullanıcı Ekleme Modal */}
      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal-content add-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-user-plus"></i>
                Yeni Kullanıcı Oluştur
              </h3>
              <button onClick={() => {
                setShowAddUserModal(false);
                setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.MONTAJCI });
                setUserFormError(null);
              }} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                {userFormError && (
                  <div className="form-error">
                    <i className="fas fa-exclamation-circle"></i>
                    {userFormError}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="displayName">
                    <i className="fas fa-user"></i>
                    Kullanıcı Adı
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={newUserForm.displayName}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, displayName: e.target.value }))}
                    placeholder="Örn: Ahmet Yılmaz"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i>
                    E-posta
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="ornek@email.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <i className="fas fa-lock"></i>
                    Şifre
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="En az 6 karakter"
                    minLength={6}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">
                    <i className="fas fa-user-tag"></i>
                    Rol
                  </label>
                  <select
                    id="role"
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value={ROLES.MONTAJCI}>Montajcı</option>
                    <option value={ROLES.YAZILIMCI}>Yazılımcı</option>
                    <option value={ROLES.SATIS_DANISMANI}>Satış Danışmanı</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="submit" className="btn-primary">
                  <i className="fas fa-user-plus"></i>
                  Kullanıcı Oluştur
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.MONTAJCI });
                    setUserFormError(null);
                  }}
                  className="btn-secondary"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Kullanıcı Silme Onay Modal */}
      {deleteUserModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                Kullanıcı Sil
              </h3>
              <button onClick={() => setDeleteUserModal(null)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                <strong>{deleteUserModal.displayName || deleteUserModal.email}</strong> kullanıcısını silmek istediğinizden emin misiniz?
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                Bu işlem geri alınamaz! Kullanıcı sisteme giriş yapamayacak. Bu Gmail bilgisi ile tekrar bir kullanıcı oluşturamayacaksınız, kullanıcı tekrar bu bilgilerle sisteme erişemeyecek.
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => handleDeleteUser(deleteUserModal.id)}
                className="btn-primary delete-confirm-btn"
              >
                <i className="fas fa-trash"></i>
                Evet, Sil
              </button>
              <button
                onClick={() => setDeleteUserModal(null)}
                className="btn-secondary"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {showPasswordConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <h2>Şifre Değiştirme Onayı</h2>
              <button 
                onClick={() => setShowPasswordConfirmModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '18px',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  padding: 0,
                  outline: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#94a3b8';
                }}
                title="Kapat"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="warning-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p style={{ marginTop: '16px', fontSize: '15px', textAlign: 'center' }}>
                Şifrenizi değiştirmek üzeresiniz. Bu işlem gerçekleştikten sonra güvenlik nedeniyle <strong>diğer tüm cihazlardaki açık oturumlarınız otomatik olarak kapatılacaktır</strong>.
              </p>
              <p style={{ marginTop: '8px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                İşleme devam etmek istediğinize emin misiniz?
              </p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
              <button
                className="btn-cancel"
                onClick={() => setShowPasswordConfirmModal(false)}
                style={{ padding: '8px 24px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'pointer' }}
              >
                İptal
              </button>
              <button
                className="btn-delete"
                onClick={confirmPasswordChange}
                style={{ padding: '8px 24px', borderRadius: '4px', border: 'none', backgroundColor: '#1a1a1a', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Onayla ve Değiştir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;